import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Buscador de contactos existentes (Cualificación + Pipeline + fichas de cliente).
 * Permite elegir uno (rellena/vincula) o escribir uno nuevo (texto libre).
 * Props: value, onChange(text), onSelect(contact), placeholder, testid, disabled
 */
export const ContactSearchInput = ({ value, onChange, onSelect, placeholder, testid, disabled }) => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const tRef = useRef(null);
  const justPicked = useRef(false);

  useEffect(() => {
    if (justPicked.current) { justPicked.current = false; return; }
    const v = (value || "").trim();
    if (v.length < 2) { setItems([]); return; }
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/crm-leads/contact-search", { params: { q: v } });
        setItems(data.items || []);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 250);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [value]);

  const pick = (c) => {
    justPicked.current = true;
    onSelect?.(c);
    setOpen(false);
    setItems([]);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={value || ""}
          disabled={disabled}
          onChange={(e) => { onChange?.(e.target.value); setOpen(true); }}
          onFocus={() => (value || "").trim().length >= 2 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 160)}
          placeholder={placeholder || "Busca o escribe un nombre…"}
          data-testid={testid}
          autoComplete="off"
          className="pl-8"
        />
      </div>
      {open && (items.length > 0 || loading) && (
        <div
          data-testid={`${testid}-suggestions`}
          className="absolute z-[60] mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg"
        >
          {loading && items.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Buscando…
            </div>
          )}
          {items.map((c) => (
            <button
              type="button"
              key={`${c.source_id}-${c.name}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(c)}
              data-testid={`${testid}-opt`}
              className="flex w-full flex-col items-start gap-1 border-b border-border/40 px-3 py-2 text-left transition-colors last:border-0 hover:bg-muted"
            >
              <span className="text-sm font-medium text-foreground">{c.name}</span>
              <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                {c.email && <span>{c.email}</span>}
                {c.destination && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{c.destination}</span>}
                {(c.sources || []).map((s) => (
                  <span key={s} className="rounded-full bg-muted px-1.5 py-0.5 ring-1 ring-border">{s}</span>
                ))}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactSearchInput;
